import React from 'react';
import { v4 as uuid } from 'uuid';
import { format } from 'date-fns';

import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import {
  Button,
  ButtonPriority,
  ThreeDotsLoader,
  Text,
  TextTypography,
  TextButton,
  SectionNotification,
  SectionNotificationSize,
  SectionNotificationType,
} from '@wix/wix-ui-tpa/cssVars';
import { joinParticipant } from '@wix/ambassador-challenges-v1-participant/http';
import { Check as CheckIcon } from '@wix/wix-ui-icons-common/on-stage';

import { st, classes } from './PaymentJoinButton.st.css';
import { useUser } from '../../../../contexts/User/UserContext';
import { usePromisifyAmbassadorRequest } from '../../../../services/asyncController/usePromisifyAmbassadorRequest';
import {
  Pages,
  useLocation,
} from '../../../../contexts/Location/LocationContext';
import { useSettings } from '@wix/tpa-settings/react';
import challengeSettings from '../../settingsParams';
import { getSecondaryTagName } from '../../utils/a11y';

export interface IPaymentJoinButtonProps {}

export const PaymentJoinButton: React.VFC<IPaymentJoinButtonProps> = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const { participant, user, requestMemberEmail, logout } = useUser();
  const join = usePromisifyAmbassadorRequest(
    joinParticipant,
    'joinParticipant',
  );
  const { goToPage } = useLocation();
  const { experiments } = useExperiments();
  const settings = useSettings();
  const secondaryTagName = getSecondaryTagName(
    settings.get(challengeSettings.titleTag),
  );

  React.useEffect(() => {
    requestMemberEmail();
  }, [requestMemberEmail]);

  const onJoinClick = React.useCallback(async () => {
    setLoading(true);

    try {
      const startDate = format(new Date(), 'yyyy-MM-dd');
      await join({
        challengeId: participant.challenge.id,
        memberId: participant.member.id,
        timeZone: participant.timezone,
        startDate,
        actionId: uuid(),
      });
      goToPage({
        pageId: Pages.ThankYou,
        challengeId: participant.challenge.id,
      });
    } catch (error) {
      console.log('Error on join participant:', error?.response || error);

      setLoading(false);
    }
  }, [participant, goToPage, join]);

  return (
    <div className={st(classes.paymentButtonContainer, {})}>
      <div className={classes.textContainer}>
        <div className={classes.titleWrapper}>
          <Text
            typography={TextTypography.largeTitle}
            tagName={secondaryTagName}
            className={classes.title}
          >
            <CheckIcon />
            <span>{t('checkout.title.sign-up')}</span>
          </Text>
          <TextButton onClick={() => logout()}>
            {t('checkout.logout')}
          </TextButton>
        </div>
        <Text typography={TextTypography.runningText}>
          {t('checkout.text.logged-in', {
            memberEmail: user.email,
          })}
        </Text>
        {experiments.enabled(
          'specs.programs.EnableFreeCouponNotificationOnCheckout',
        ) ? (
          <SectionNotification
            className={classes.couponAppliedNotification}
            size={SectionNotificationSize.compact}
            type={SectionNotificationType.wired}
          >
            <SectionNotification.Text>
              {t('checkout.coupon-applied.free-join')}
            </SectionNotification.Text>
          </SectionNotification>
        ) : null}
      </div>
      <Button
        className={classes.btn}
        onClick={onJoinClick}
        priority={ButtonPriority.primary}
        disabled={loading}
        fullWidth={true}
      >
        {loading ? (
          <span className={classes.loader}>
            <ThreeDotsLoader />
          </span>
        ) : (
          t('coupons.button.join-program')
        )}
      </Button>
    </div>
  );
};
